import { useEffect, useState } from "react";
import { getSoftware } from "../lib/software";

/* ================= LOGOS ================= */
const SOFTWARE_LOGOS = {
  AEC: "/software-logos/aec.png",
  Revit: "/software-logos/revit.png",
  SketchUp: "/software-logos/sketchup.png",
  Adobe: "/software-logos/adobe.png",
  Bluebeam: "/software-logos/bluebeam.png",
  Microsoft365: "/software-logos/microsoft.png",
  D5: "/software-logos/d5.png",
  ProjectPlan3: "/software-logos/project-plan-3.png",
  Primus: "/software-logos/primus.jpg",
  cadmate: "/software-logos/cadmate.png",
  BricsCAD: "/software-logos/bricscad.png",
};

export default function SoftwareEmployee() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getSoftware()
      .then((res) => setData(res.data || []))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div
      style={{
        padding: 28,
        minHeight: "100vh",
        background: "radial-gradient(circle at top, #f8faff, #eef2ff)",
      }}
    >
      {/* PAGE HEADER */}
      <h2
        style={{
          fontSize: 22,
          fontWeight: 800,
          color: "#0f172a",
          marginBottom: 24,
        }}
      >
        My Software
      </h2>

      {/* LOADING */}
      {loading && (
        <div style={{ padding: 20, color: "#64748b" }}>Loading software...</div>
      )}

      {/* EMPTY */}
      {!loading && data.length === 0 && (
        <div style={{ padding: 20, color: "#64748b" }}>
          No software assigned
        </div>
      )}

      {/* SOFTWARE CARDS */}
      {!loading && data.length > 0 && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(340px, 1fr))",
            gap: 22,
          }}
        >
          {data.map((item) => {
            const expiry = getExpiryInfo(item.endDate);

            return (
              <div
                key={item._id}
                style={{
                  background: "#ffffff",
                  borderRadius: 22,
                  boxShadow: "0 18px 40px rgba(15,23,42,0.12)",
                  overflow: "hidden",
                }}
              >
                {/* ===== HEADER ===== */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "22px 24px",
                  }}
                >
                  <div>
                    <div
                      style={{
                        fontSize: 18,
                        fontWeight: 900,
                        color: "#0f172a",
                        marginBottom: 4,
                      }}
                    >
                      {item.softwareName} SOFTWARE
                    </div>

                    <div
                      style={{
                        fontSize: 14,
                        fontWeight: 500,
                        color: "#64748b",
                      }}
                    >
                      {item.softwareType || "—"}
                    </div>
                  </div>

                  <div
                    style={{
                      width: 86,
                      height: 86,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <img
                      src={SOFTWARE_LOGOS[item.softwareName]}
                      alt={item.softwareName}
                      style={{
                        maxWidth: "100%",
                        maxHeight: "100%",
                        objectFit: "contain",
                      }}
                    />
                  </div>
                </div>

                {/* ===== BODY ===== */}
                <div style={{ padding: 20 }}>
                  {/* VERSION */}
                  <div
                    style={{
                      fontSize: 13,
                      color: "#475569",
                      marginBottom: 12,
                    }}
                  >
                    Version {item.version || "—"}
                  </div>

                  {/* ALL DETAILS (UNCHANGED) */}
                  <InfoRow label="PC Name" value={item.pcName} />
                  <InfoRow label="IP Address" value={item.ipAddress} />

                  <InfoRow
                    label="Assigned On"
                    value={
                      item.assignedAt
                        ? new Date(item.assignedAt).toLocaleDateString(
                            "en-IN",
                            {
                              day: "2-digit",
                              month: "short",
                              year: "numeric",
                            }
                          )
                        : "—"
                    }
                  />

                  <InfoRow
                    label="Expiry Date"
                    value={
                      item.endDate
                        ? new Date(item.endDate).toLocaleDateString("en-IN", {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                          })
                        : "PERMANENT"
                    }
                  />

                  {/* ===== DAYS COUNT (NEW, EXTRA) ===== */}
                  <div
                    style={{
                      marginTop: 14,
                      padding: "12px 14px",
                      borderRadius: 14,
                      textAlign: "center",
                      fontSize: 15,
                      fontWeight: 800,
                      background: expiry.bg,
                      color: expiry.color,
                    }}
                  >
                    ⏳ {expiry.text}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ================= ROW ================= */
function InfoRow({ label, value }) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        padding: "7px 0",
        fontSize: 14,
        borderBottom: "1px dashed #e5e7eb",
      }}
    >
      <span style={{ color: "#64748b" }}>{label}</span>
      <strong style={{ color: "#0f172a", marginLeft: 12 }}>
        {value || "—"}
      </strong>
    </div>
  );
}

/* ================= DAYS COUNT LOGIC ================= */
function getExpiryInfo(endDate) {
  if (!endDate) {
    return {
      text: "Lifetime License",
      color: "#1d4ed8",
      bg: "#e0e7ff",
    };
  }

  const today = new Date();
  const expiry = new Date(endDate);
  const diff = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));

  if (diff < 0) {
    return {
      text: "Expired",
      color: "#dc2626",
      bg: "#fee2e2",
    };
  }

  if (diff <= 30) {
    return {
      text: `${diff} Days Left`,
      color: "#ea580c",
      bg: "#ffedd5",
    };
  }

  return {
    text: `${diff} Days Left`,
    color: "#16a34a",
    bg: "#dcfce7",
  };
}
